import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, ModuleBase, ProgressCircular, RichText, Stack, UserContext } from '@/components';
import { LinkModel, ModuleBaseModel, ModuleSectionPropsModel } from '@/types';
import classNames from 'classnames';
import { animControllerInView, fadeIn } from '@/animations';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import { usePathname } from 'next/navigation';
export type RichTextModuleModel = ModuleBaseModel<
  {
    fitToPageWidth?: boolean;
    hideFromPDF?: boolean;
    text: string;
    link?: LinkModel[];
  } & ModuleSectionPropsModel
>;

const RichTextModule = (data: RichTextModuleModel) => {
  const user = useContext(UserContext);
  const salesforceId = user?.salesforceId;
  const pathname = usePathname();
  const container = useRef<HTMLDivElement>(null);

  const isPLDPage = useMemo(
    () => pathname === '/tools/product-lifecycle-database' && data.props.text.includes('{USER_SALES_FORCE_ID}'),
    [pathname, data.props.text],
  );

  useEffect(() => {
    if (isPLDPage) {
      const iframe = container.current?.getElementsByTagName('iframe')[0];
      iframe?.addEventListener('load', () => {
        const loadingIndicator = container.current?.getElementsByClassName('loading-indicator')[0];
        if (loadingIndicator) {
          loadingIndicator.classList.add('hidden');
        }
      });
    }
  }, [isPLDPage]);

  const text = useMemo(() => {
    const sourceText = data.props.text;
    if (!salesforceId || !sourceText.includes('{USER_SALES_FORCE_ID}')) {
      return sourceText;
    }

    return sourceText.replaceAll('{USER_SALES_FORCE_ID}', salesforceId);
  }, [data.props.text, salesforceId]);

  return (
    <ModuleBase data={data} className={classNames(data.props.hideFromPDF && 'print:hidden')}>
      <Stack
        className={classNames(
          isPLDPage
            ? 'relative mx-auto max-w-[1800px] px-[var(--container-padding)] md:px-[var(--container-padding-md)]'
            : 'container',
        )}
        animation={animControllerInView({ amount: 'some' })}
        ref={container}
      >
        {isPLDPage && (
          <div className="loading-indicator absolute flex h-[280px] w-full items-center justify-center">
            <ProgressCircular indeterminate size={50} width={4} colorClassName="stroke-sky" />
          </div>
        )}

        <RichText
          animate
          animation={fadeIn}
          data={text}
          className={classNames(
            'color-from-bg mx-auto flex w-full flex-col justify-center pb-3',
            !data.props.fitToPageWidth && 'lg:w-3/4 lg:px-6',
          )}
        />
        {data.props.link && (
          <div className="flex gap-3">
            {data.props.link.map((link, index) => (
              <Link className="btn secondary btn-from-bg !tracking-normal hover:bg-[#f5bda2]" key={index} link={link}>
                {link.text}
                <ArrowRightLight className="h-6 w-6" />
              </Link>
            ))}
          </div>
        )}
      </Stack>
    </ModuleBase>
  );
};

export default RichTextModule;
